import { Component, OnDestroy, OnInit } from '@angular/core';
import { OpenChoiceSelectedAccount } from '@interfaces/open-choice/multiple-accounts.model';
import {
  PlaceholderAccount,
  ProjectionDetails,
  ProspectAccount,
} from '@interfaces/open-choice/projection-details.model';
import { select, Store } from '@ngrx/store';
import { PageComponent } from '@shared/components';
import { ContentPageId, FlowTypes, urlMapper } from '@shared/models';
import { MultiAccountEnrollmentContent } from '@shared/services/content/content-jsons/open-choice/multi-account-enrollment.content';
import { OpenChoiceCommonContent } from '@shared/services/content/content-jsons/open-choice/open-choice-common.content';
import {
  Go,
  initializeOnboarding,
  LogError,
  Next,
  rejectDAOffer,
  selectTriageState,
  State,
  TrackProcessSideStep,
  UpdateDAAccountsToEnroll,
  UpdateDAComponentFailure,
} from '@store/index';
import { getFlowTypeSelection } from '@store/reducers/triage/triage.reducer';

@Component({
  selector: 'app-multi-account-enrollment',
  templateUrl: './multi-account-enrollment.component.html',
  styleUrl: './multi-account-enrollment.component.scss',
})
export class MultiAccountEnrollmentComponent extends PageComponent implements OnInit, OnDestroy {
  content = MultiAccountEnrollmentContent;
  commonContent = OpenChoiceCommonContent;
  flow: FlowTypes | null = null;
  accountsSelected: number = 0;
  processedAccounts: ProjectionDetails = { placeholderAccounts: [] };

  constructor(globalStore: Store<State>) {
    super(ContentPageId.multiaccountenrollemnt, globalStore);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.add(
      this.globalStore.pipe(select(selectTriageState)).subscribe((triage) => {
        this.flow = getFlowTypeSelection(triage);
      }),
    );
  }

  transformToProjectionDetails(account: OpenChoiceSelectedAccount): PlaceholderAccount {
    return {
      accountNameToDisplay: account.accountName || '',
      accountNumberToDisplay: account.brokerageAccountNumber || '',
      accountTypeToDisplay: account.accountType || '',
      totalAmountToDisplay: account.balance || 0,
      accountId: ProspectAccount.PROSPECT_ACCOUNT_NAME,
    };
  }

  handleSelectedAccountsToEnroll(event: CustomEvent): void {
    const accounts = event?.detail || [];
    this.accountsSelected = accounts.length;
    this.processedAccounts = {
      ...this.processedAccounts,
      placeholderAccounts: accounts.map((account: OpenChoiceSelectedAccount) =>
        this.transformToProjectionDetails(account),
      ),
    };
    this.globalStore.dispatch(UpdateDAAccountsToEnroll({ payload: this.processedAccounts }));
  }

  errorHandler(event: CustomEvent) {
    const errorDetails = JSON.stringify(event.detail);
    this.globalStore.dispatch(
      LogError({
        payload: {
          eventId: 'open-choice-multi-account-enrollment--error',
          errorDetails,
        },
      }),
    );
    this.globalStore.dispatch(rejectDAOffer());
    this.globalStore.dispatch(UpdateDAComponentFailure({ payload: true }));
    if (this.flow === FlowTypes.MINOR) {
      this.globalStore.dispatch(Go({ path: [`secure/${urlMapper[this.flow]}/review`] }));
    } else {
      this.globalStore.dispatch(Go({ path: ['error'] }));
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  nextPage(): void {
    if (this.accountsSelected > 0) {
      this.globalStore.dispatch(Next());
      this.adobeProcessSideStepSuccess();
    }
  }

  adobeProcessSideStepSuccess(): void {
    const data = {
      processType: `coo-minor-flow`,
      processSideStep: `coo-minor-gc-${this.accountsSelected > 1 ? 'multi_accounts_enrolled-' + this.accountsSelected : 'single_account_enrolled'}`,
    };

    this.globalStore.dispatch(TrackProcessSideStep(data));
  }
}
